import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  message,
  Tooltip,
  Typography,
  Card,
  Input,
  Spin,
  Checkbox,
  Tag,
  DatePicker,
} from "antd";
import { Link } from "react-router-dom";
import axios from "axios";
import { adalApiFetch } from ".././adalConfig";
import * as api from ".././constants/api";
import "antd/dist/reset.css";
import { CopyOutlined, SearchOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
const { Title } = Typography;
const { Search } = Input;

// Status options for Error Status column
const statusOptions = [
  { value: "Pending", label: "Pending" },
  { value: "In Progress", label: "In Progress" },
  { value: "Completed", label: "Completed" },
];

// Move columns definition inside component to access handleStatusChange

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {
    message.success("Copied to clipboard!");
  });
};

const Errors4003 = () => {
  const [searchText, setSearchText] = useState("");
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [loading, setLoading] = useState(false);
  const [errorStatuses, setErrorStatuses] = useState({}); // Local state to track status changes
  const [errorModifiedDates, setErrorModifiedDates] = useState({}); // Local state to track modified dates
  const [errorResolutionNotes, setErrorResolutionNotes] = useState({}); // Local state to track resolution notes
  const [jiraValues, setJiraValues] = useState({}); // Local state to track Jira field values
  const [jiraEditingStates, setJiraEditingStates] = useState({}); // Local state to track which Jira fields are being edited
  const [savingStates, setSavingStates] = useState({}); // Local state to track saving status
  const [hasChanges, setHasChanges] = useState({}); // Local state to track if row has unsaved changes
  const [openDatePickers, setOpenDatePickers] = useState({}); 

  // Handle status change for individual error records
  const handleStatusChange = (errorId, statusValue, checked) => {
    setErrorStatuses((prev) => ({
      ...prev,
      [errorId]: checked ? statusValue : "Pending", // Default to Pending if unchecked
    }));

    setErrorModifiedDates((prev) => ({
      ...prev,
      [errorId]: null,
    }));

    // Open the date picker for any status change
    setOpenDatePickers((prev) => ({
      ...prev,
      [errorId]: true,
    }));

    // Mark row as having changes
    setHasChanges((prev) => ({
      ...prev,
      [errorId]: true,
    }));

    // TODO: Here you would make an API call to update the backend
    // For now, we're just updating the local state
    const statusText = checked ? statusValue : "Pending";
    console.log(`Updating error ${errorId} status to ${statusText}`);
  };

  // Handle date change for error modified date
  const handleDateChange = (errorId, date) => {
    setErrorModifiedDates((prev) => ({
      ...prev,
      [errorId]: date,
    }));

    // Mark row as having changes
    setHasChanges((prev) => ({
      ...prev,
      [errorId]: true,
    }));
  };

  // Handle notes change for error resolution notes
  const handleNotesChange = (errorId, notes) => {
    setErrorResolutionNotes((prev) => ({
      ...prev,
      [errorId]: notes,
    }));

    // Mark row as having changes
    setHasChanges((prev) => ({
      ...prev,
      [errorId]: true,
    }));
  };

  // Handle Jira field change
  const handleJiraChange = (errorId, jiraValue) => {
    // Extract ticket ID if user pastes full URL
    let cleanedValue = jiraValue;
    const jiraUrlPattern = /https?:\/\/jirasw\.nvidia\.com\/browse\//gi;
    if (jiraUrlPattern.test(jiraValue)) {
      cleanedValue = jiraValue.replace(jiraUrlPattern, "");
    }

    // Sanitize input: Remove any potentially malicious characters
    // Only allow alphanumeric characters, hyphens, and underscores (standard Jira ticket format)
    cleanedValue = cleanedValue.replace(/[^a-zA-Z0-9\-_]/g, "");

    // Validate Jira ticket format (PROJECT-123 or PROJECT_123)
    // Typical format: 1-10 uppercase letters, followed by hyphen/underscore, followed by 1-6 digits
    const jiraTicketPattern = /^[A-Z]{1,10}[-_]\d{1,6}$/;

    // If value is not empty and doesn't match pattern, show warning
    if (cleanedValue && !jiraTicketPattern.test(cleanedValue)) {
      // Allow partial input while typing, but validate on blur
      // For now, just sanitize and let user continue typing
    }

    setJiraValues((prev) => ({
      ...prev,
      [errorId]: cleanedValue,
    }));

    // Mark row as having changes
    setHasChanges((prev) => ({
      ...prev,
      [errorId]: true,
    }));
  };

  // Validate Jira ticket format on blur
  const validateJiraTicket = (errorId, jiraValue) => {
    if (!jiraValue) return true; // Empty is allowed (optional field)

    const jiraTicketPattern = /^[A-Z]{1,10}[-_]\d{1,6}$/;
    if (!jiraTicketPattern.test(jiraValue)) {
      message.warning(`Invalid Jira ticket format for ID ${errorId}.`);
      return false;
    }
    return true;
  };

  // Handle save changes for individual error record
  const handleSaveChanges = async (errorId) => {
    // Validate Jira ticket before saving
    const jiraValue = jiraValues[errorId] || "";
    if (jiraValue && !validateJiraTicket(errorId, jiraValue)) {
      return; // Don't save if validation fails
    }

    setSavingStates((prev) => ({
      ...prev,
      [errorId]: true,
    }));

    try {
      // Prepare complete payload with all editable fields
      const saveData = {
        errorId: errorId,
        errorStatus: errorStatuses[errorId] || "Pending",
        errorModifiedDate: errorModifiedDates[errorId]
          ? dayjs(errorModifiedDates[errorId]).format("YYYY-MM-DD")
          : null,
        errorResolutionNotes: errorResolutionNotes[errorId] || "",
        jiraTicket: jiraValue, // Jira ticket ID (optional)
      };

      console.log("Saving data for error:", errorId, saveData);

      await adalApiFetch(axios, api.POST_UPDATE_ERROR, {
        method: "POST",
        data: saveData,
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Mark as saved (reset change tracking)
      setHasChanges((prev) => ({
        ...prev,
        [errorId]: false,
      }));

      message.success(`Error ${errorId} saved successfully!`);
    } catch (error) {
      console.error("Error saving:", error);
      message.error(`Failed to save error ${errorId}. ${error.message || ""}`);
    } finally {
      setSavingStates((prev) => ({
        ...prev,
        [errorId]: false,
      }));
    }
  };

  // Define columns inside component to access handleStatusChange
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      sorter: true,
      width: "5%",
    },
    {
      title: "Jira",
      dataIndex: "jiraValue",
      width: "10%",
      render: (text, record) => {
        const currentJiraValue = jiraValues[record.id] || "";
        const trimmedValue = currentJiraValue.replace(/\s+/g, ""); // Remove all spaces
        const jiraUrl = trimmedValue
          ? `https://jirasw.nvidia.com/browse/${trimmedValue}`
          : "";
        const isEditing = jiraEditingStates[record.id] || false;

        // Show input field when editing, otherwise show link
        if (isEditing || !trimmedValue) {
          return (
            <Input
              size="small"
              value={currentJiraValue}
              onChange={(e) => handleJiraChange(record.id, e.target.value)}
              onKeyDown={(e) => {
                // Allow: backspace, delete, tab, escape, enter, arrows, home, end
                const allowedKeys = [
                  "Backspace",
                  "Delete",
                  "Tab",
                  "Escape",
                  "Enter",
                  "ArrowLeft",
                  "ArrowRight",
                  "ArrowUp",
                  "ArrowDown",
                  "Home",
                  "End",
                ];

                // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Cmd+A, Cmd+C, Cmd+V, Cmd+X
                if (e.ctrlKey || e.metaKey) {
                  if (
                    ["a", "c", "v", "x", "A", "C", "V", "X"].includes(e.key)
                  ) {
                    return; // Allow copy/paste/select all
                  }
                }

                // If it's a special key, allow it
                if (allowedKeys.includes(e.key)) {
                  return;
                }

                // Only allow alphanumeric, hyphen, and underscore
                const validCharPattern = /^[a-zA-Z0-9\-_]$/;
                if (!validCharPattern.test(e.key)) {
                  e.preventDefault(); // Block the key
                  return;
                }
              }}
              onPaste={(e) => {
                // Handle paste event to clean pasted content
                e.preventDefault();
                const pastedText = e.clipboardData.getData("text");

                // Check if it's a URL
                const urlPattern = /^https?:\/\//i;
                if (urlPattern.test(pastedText)) {
                  // It's a URL - check if it's a valid Jira URL
                  const jiraUrlPattern =
                    /^https?:\/\/jirasw\.nvidia\.com\/browse\/.+/gi;
                  if (jiraUrlPattern.test(pastedText)) {
                    // Valid Jira URL - extract ticket ID
                    const cleanedValue = pastedText.replace(
                      /https?:\/\/jirasw\.nvidia\.com\/browse\//gi,
                      ""
                    );
                    handleJiraChange(record.id, cleanedValue);
                  } else {
                    // Invalid URL - reject it
                    message.error(
                      "Only Jira URLs from jirasw.nvidia.com are allowed!"
                    );
                  }
                  return;
                }

                // Not a URL - sanitize and allow
                let cleanedValue = pastedText;
                // Remove any invalid characters
                cleanedValue = cleanedValue.replace(/[^a-zA-Z0-9\-_]/g, "");

                // Update the value
                handleJiraChange(record.id, cleanedValue);
              }}
              onBlur={(e) => {
                // Remove spaces when user clicks outside
                const cleanedValue = e.target.value.replace(/\s+/g, "");
                if (cleanedValue !== e.target.value) {
                  handleJiraChange(record.id, cleanedValue);
                }

                // Validate the Jira ticket format
                const finalValue = jiraValues[record.id] || cleanedValue;
                validateJiraTicket(record.id, finalValue);

                setJiraEditingStates((prev) => ({
                  ...prev,
                  [record.id]: false,
                }));
              }}
              onFocus={() =>
                setJiraEditingStates((prev) => ({
                  ...prev,
                  [record.id]: true,
                }))
              }
              placeholder="Enter Jira ticket"
              style={{ fontSize: "10px" }}
            />
          );
        }

        // Show hyperlink when not editing and value exists
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "4px",
              padding: "4px 8px",
              border: "1px solid #d9d9d9",
              borderRadius: "2px",
              minHeight: "24px",
            }}
          >
            <a
              href={jiraUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: "10px",
                color: "#1890ff",
                textDecoration: "underline",
                wordBreak: "break-all",
                flex: 1,
              }}
            >
              {trimmedValue}
            </a>
            <EditOutlined
              style={{
                fontSize: "12px",
                color: "#1890ff",
                cursor: "pointer",
                flexShrink: 0,
              }}
              onClick={() =>
                setJiraEditingStates((prev) => ({
                  ...prev,
                  [record.id]: true,
                }))
              }
            />
          </div>
        );
      },
    },
    {
      title: "ErrorMsg",
      dataIndex: "exception",
      width: "25%",
    },
    {
      title: "ErrorOrigin",
      dataIndex: "methodName",
      width: "15%",
      render: (text) => text.replace("Post", ""),
    },
    {
      title: "Data",
      dataIndex: "data",
      width: "15%",
      ellipsis: true,
      render: (text) => (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Tooltip title={text}>
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
              }}
            >
              {text}
            </span>
          </Tooltip>
          <Button
            type="link"
            icon={<CopyOutlined />}
            onClick={() => copyToClipboard(text)}
            style={{ marginLeft: 8, flexShrink: 0 }}
          />
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "createdDate",
      width: "15%",
    },
    {
      title: "Error Status",
      dataIndex: "errorStatus",
      width: "15%",
      render: (text, record) => {
        const currentStatus = errorStatuses[record.id] || text || "Pending"; 
        const savedStatus = text || "Pending";
        const isCompletedAndSaved = savedStatus === "Completed"; 

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {statusOptions.map((option) => {
              // If status is saved as "Completed" in database, only show the "Completed" option
              if (isCompletedAndSaved && option.value !== "Completed") {
                return null;
              }

              const isThisOptionCompletedAndSaved =
                isCompletedAndSaved && option.value === "Completed";

              return (
                <div
                  key={option.value}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    opacity: isThisOptionCompletedAndSaved ? 0.6 : 1, // Grey out only when completed AND saved
                  }}
                >
                  <Checkbox
                    checked={currentStatus === option.value}
                    onChange={(e) =>
                      handleStatusChange(
                        record.id,
                        option.value,
                        e.target.checked
                      )
                    }
                    size="small"
                    className="green-checkbox"
                    disabled={isThisOptionCompletedAndSaved} // Disable only if completed AND saved
                  />
                  <Tag
                    style={{
                      margin: 0,
                      fontSize: "9px",
                      padding: "2px 6px",
                      lineHeight: "1.2",
                      border: isThisOptionCompletedAndSaved
                        ? "1px solid #d9d9d9"
                        : "1px solid black",
                      backgroundColor: isThisOptionCompletedAndSaved
                        ? "#f5f5f5"
                        : "white",
                      color: isThisOptionCompletedAndSaved
                        ? "#8c8c8c"
                        : "black",
                      width: "80px",
                      textAlign: "center",
                      display: "inline-block",
                      height: "20px",
                      boxSizing: "border-box",
                      cursor: isThisOptionCompletedAndSaved
                        ? "not-allowed"
                        : "default",
                    }}
                  >
                    {option.label}
                  </Tag>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: "Error Modified Date",
      dataIndex: "errorModifiedDate",
      width: "15%",
      render: (text, record) => {
        const currentDate = errorModifiedDates[record.id] || null;
        const isOpen = openDatePickers[record.id] || false;
        return (
          <DatePicker
            size="small"
            value={currentDate ? dayjs(currentDate) : null}
            open={isOpen}
            onChange={(date) => {
              handleDateChange(record.id, date);
              // Close the date picker after selection
              setOpenDatePickers((prev) => ({
                ...prev,
                [record.id]: false,
              }));
            }}
            onOpenChange={(open) => {
              // Update the open state when user manually opens/closes
              setOpenDatePickers((prev) => ({
                ...prev,
                [record.id]: open,
              }));
            }}
            placeholder="Select date"
            style={{ width: "100%" }}
            format="MM/DD/YYYY"
          />
        );
      },
    },
    {
      title: "Error Resolution Notes",
      dataIndex: "errorResolutionNotes",
      width: "15%",
      render: (text, record) => {
        const currentNotes = errorResolutionNotes[record.id] || "";
        return (
          <Input.TextArea
            size="small"
            value={currentNotes}
            onChange={(e) => handleNotesChange(record.id, e.target.value)}
            placeholder="Enter resolution notes..."
            rows={2}
            style={{ fontSize: "10px" }}
          />
        );
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      width: "10%",
      render: (text, record) => {
        const isSaving = savingStates[record.id] || false;
        const hasUnsavedChanges = hasChanges[record.id] || false;

        return (
          <Button
            type="primary"
            size="small"
            loading={isSaving}
            disabled={!hasUnsavedChanges}
            onClick={() => handleSaveChanges(record.id)}
            style={{
              backgroundColor: hasUnsavedChanges ? "#52c41a" : "#d9d9d9",
              borderColor: hasUnsavedChanges ? "#52c41a" : "#d9d9d9",
              fontSize: "10px",
              height: "24px",
              padding: "0 8px",
            }}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        );
      },
    },
  ];

  const handleSearch = (value) => {
    setSearchText(value); // Update search state
    fetchData(value);
  };
  const buildFilter = () => {
    let filterString = "";

    if (searchText) {
      filterString += "Data.Contains(" + JSON.stringify(searchText) + ")";
    }

    return filterString;
  };
  // ✅ Move fetchData ABOVE useEffect so it's properly recognized
  const fetchData = ({ results, page }) => {
    setLoading(true);

    let filterString = buildFilter();

    let options = {
      params: {
        take: results || 10,
        skip: results * (page - 1) || 0,
        orderby: "id desc",
        filter: filterString,
      },
    };

    adalApiFetch(axios, api.GET_REGISTRATIONERRORS, options)
      .then((response) => {
        debugger;
        const modifiedData = response.data.records.map((r) => ({
          ...r,
          createdDate: new Date(r.createdDate).toLocaleDateString(),
          entitlement: r.pakid && r.pakid !== "" ? r.pakid : r.serialNumber,
          errorStatus: r.errorStatus || "Pending", // Initialize with 'Pending' if no status from API
        }));

        setData(modifiedData);

        // Initialize state variables from API response
        const initialStatuses = {};
        const initialDates = {};
        const initialNotes = {};
        const initialJira = {};

        response.data.records.forEach((record) => {
          if (record.errorStatus) {
            initialStatuses[record.id] = record.errorStatus;
          }
          if (record.errorModifiedDate) {
            initialDates[record.id] = dayjs(record.errorModifiedDate);
          }
          if (record.errorResolutionNotes) {
            initialNotes[record.id] = record.errorResolutionNotes;
          }
          if (record.jiraTicket) {
            initialJira[record.id] = record.jiraTicket;
          }
        });

        setErrorStatuses(initialStatuses);
        setErrorModifiedDates(initialDates);
        setErrorResolutionNotes(initialNotes);
        setJiraValues(initialJira);

        setPagination((prev) => ({
          ...prev,
          total: response.data.totalCount,
          current: page,
        }));
      })
      .catch((error) => console.error(error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData({ results: pagination.pageSize, page: pagination.current });
  }, []); // Empty dependency array = Runs only on mount

  const handleTableChange = (pagination) => {
    fetchData({ results: pagination.pageSize, page: pagination.current });
  };

  return (
    <Card>
      <style>
        {`
          .green-checkbox .ant-checkbox-checked .ant-checkbox-inner {
            background-color: #52c41a !important;
            border-color: #52c41a !important;
          }
          .green-checkbox .ant-checkbox-checked .ant-checkbox-inner::after {
            border-color: white !important;
          }
          .green-checkbox:hover .ant-checkbox-inner {
            border-color: #52c41a !important;
          }
        `}
      </style>
      <Title level={4} style={{ marginBottom: 2 }}>
        Registration Error Details
      </Title>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "left",
          marginBottom: 20,
          marginLeft: 20,
        }}
      >
        <Search
          placeholder="Search by data"
          enterButton={
            <Button
              type="primary"
              style={{ backgroundColor: "#76b900", borderColor: "#76b900" }}
            >
              <SearchOutlined />
            </Button>
          }
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          style={{ marginBottom: 20, marginTop: 20, width: 300 }}
        />
      </div>

      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "left",
          marginBottom: 20,
        }}
      >
        <Table
          size="small"
          rowClassName={() => "rowClassName1"}
          bordered
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
    </Card>
  );
};

export default Errors4003;
